<script>
	import Dialog from "../elements/Dialog.svelte";

	/*** portrait selection: pan/zoom the chosen image under a round frame, then hand back the square cut-out ***/
	// usage: <PortraitDialog bind:this={picker} id=... onselect={file => ...}/>, then picker.pick() opens the file chooser
	let { id = 'portrait-dialog', title = "Portrait auswählen", onselect } = $props()

	const cropFrame = 360			// size of the square stage in px
	const cropCircle = 300			// diameter of the portrait circle, centred in the stage
	const cropOutput = 512			// resolution of the cut-out (server rescales and masks it round)
	const circleOffset = (cropFrame - cropCircle) / 2

	let imageInput, cropDialog, cropZoom
	let cropImage = $state(null)
	let cropSrc = $state(null)		// object URL of the selected image file
	let cropName = ""
	let zoom = $state(1), minZoom = $state(0.1), maxZoom = $state(4)

	/** opens the file chooser; the selected image is shown in the crop dialog **/
	export function pick() {
		imageInput.click()
	}

	function select_portrait(e) {
		e.stopPropagation()		// keeps enclosing forms from treating the file as an attribute change
		const file = e.target.files[0]
		e.target.value = ""		// allows re-selecting the same file
		if (!file)  return
		if (cropSrc)  URL.revokeObjectURL(cropSrc)
		cropName = file.name.replace(/\.[^.]*$/, "") || "portrait"
		cropSrc = URL.createObjectURL(file)
		cropDialog = document.getElementById(id)
		cropDialog.showModal()
	}

	/** initial placement: image just covers the circle, centred **/
	function fit_image() {
		const {naturalWidth: w, naturalHeight: h} = cropImage
		const cover = cropCircle / Math.min(w, h)
		minZoom = cover / 2
		maxZoom = cover * 8
		cropZoom.setTransform({scale: cover, x: (cropFrame - w * cover) / 2, y: (cropFrame - h * cover) / 2})
		zoom = cover
	}

	/** zoom slider: scale around the centre of the circle **/
	function zoom_to(scale) {
		const c = cropFrame / 2
		const ratio = scale / cropZoom.scale
		cropZoom.setTransform({scale, x: c + (cropZoom.x - c) * ratio, y: c + (cropZoom.y - c) * ratio})
	}

	/** cuts the square around the circle out of the original image and returns it as PNG file **/
	async function apply_portrait() {
		const {x, y, scale} = cropZoom
		const canvas = document.createElement('canvas')
		canvas.width = canvas.height = cropOutput
		canvas.getContext('2d').drawImage(cropImage,
			(circleOffset - x) / scale, (circleOffset - y) / scale, cropCircle / scale, cropCircle / scale,
			0, 0, cropOutput, cropOutput)
		const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'))
		const file = new File([blob], `${cropName}.png`, {type: 'image/png'})
		close_portrait()
		await onselect?.(file)
	}

	function close_portrait() {
		cropDialog?.close()
		if (cropSrc)  URL.revokeObjectURL(cropSrc)
		cropSrc = null
	}
</script>

<!-------------------------------- HTML -------------------------------------->

<input bind:this={imageInput} class="hidden" type="file" accept="image/*" onchange={select_portrait}>

<Dialog {id} {title}
        actions={[{id: 'cancel', label: "Abbrechen", func: close_portrait},
                  {id: 'crop', label: "Ausschnitt festlegen", symbol: 'check', shown: true, func: apply_portrait}]}>
	<div class="dlg-body crop-body">
		<div class="crop-stage">
			<pinch-zoom bind:this={cropZoom} class="crop-zoom" onchange={() => zoom = cropZoom.scale}>
				{#if cropSrc}
					<img bind:this={cropImage} class="crop-image pinchable" src={cropSrc} alt="Auswahl" draggable="false" onload={fit_image}/>
				{/if}
			</pinch-zoom>
			<div class="crop-circle"></div>
		</div>
		<label class="crop-zoom-bar">
			<span class="fa icon-minus"></span>
			<input class="crop-slider" type="range" min={minZoom} max={maxZoom} step="any" value={zoom}
			       oninput={(e) => zoom_to(+e.target.value)}/>
			<span class="fa icon-plus"></span>
		</label>
		<p class="crop-hint">Bild verschieben und zoomen, bis der Kreis den gewünschten Ausschnitt zeigt.</p>
	</div>
</Dialog>


<!-------------------------------- STYLE -------------------------------------->

<style lang="sass">
	@use "../colors" as colors

	.hidden
		display: none

	/* stage size = cropFrame, circle = cropCircle */
	.crop-body
		display: flex
		flex-direction: column
		align-items: center
		gap: 14px

	.crop-stage
		position: relative
		width: 360px
		height: 360px
		background: colors.$grey-15
		overflow: hidden

	.crop-zoom
		width: 100%
		height: 100%
		cursor: grab

		&:active
			cursor: grabbing

	.crop-image
		display: block
		max-width: none
		user-select: none

	.crop-circle
		position: absolute
		inset: 30px
		border: 2px solid colors.$white
		border-radius: 50%
		box-shadow: 0 0 0 200px rgba(0, 0, 0, .6)
		pointer-events: none

	.crop-zoom-bar
		display: flex
		align-items: center
		gap: 10px
		width: 360px
		color: colors.$grey-85

		.fa
			filter: none

	.crop-slider
		flex: 1
		padding: 0
		background: none
		accent-color: colors.$primary-100

	.crop-hint
		max-width: 360px
		padding: 0
		font-size: 12px
		color: colors.$grey-60
		line-height: unset
</style>
